import React from 'react'
import { Paper, Popper } from '@mui/material'
import { isOverflown } from 'particles/utils'
import { useStyles } from './TagCell'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import Text from 'particles/Text'
import styled, { useTheme } from 'particles/themes/styled'
import { SUBMISSION_STATUS_ENUM } from 'state/submissions'

const PayoutStatusCell = React.memo(function GridCellExpand(props: GridRenderCellParams) {
    const wrapper = React.useRef<HTMLDivElement | null>(null)
    const cellDiv = React.useRef(null)
    const cellParams = props
    const cellValue = React.useRef<HTMLDivElement | null>(null)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const classes = useStyles()
    const [showFullCell, setShowFullCell] = React.useState(false)
    const [showPopper, setShowPopper] = React.useState(false)
    const theme = useTheme()
    const width = cellParams.colDef.width

    const handleMouseEnter = () => {
        const isCurrentlyOverflown = isOverflown(cellValue.current!)
        setShowPopper(isCurrentlyOverflown)
        setAnchorEl(cellDiv.current)
        setShowFullCell(true)
    }

    const handleMouseLeave = () => {
        setShowFullCell(false)
    }

    React.useEffect(() => {
        if (!showFullCell) {
            return undefined
        }

        function handleKeyDown(nativeEvent: KeyboardEvent) {
            // IE11, Edge (prior to using Bink?) use 'Esc'
            if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
                setShowFullCell(false)
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [setShowFullCell, showFullCell])

    if (props.value.paymentStatus === SUBMISSION_STATUS_ENUM.REJECTED)
        return <Text color="error">{cellParams.value.status.rejected}</Text>

    if (props.value.paymentStatus === SUBMISSION_STATUS_ENUM.IN_PROGRESS)
        return <Text color="error">{cellParams.value.status.incomplete}</Text>

    if (props.value.paymentStatus === SUBMISSION_STATUS_ENUM.FINISHED)
        return <Text color="warning">{cellParams.value.status.awaiting}</Text>

    if (props.value.paymentStatus === SUBMISSION_STATUS_ENUM.PAID && props.value.isPaidOutsideApp) {
        return (
            <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} ref={wrapper} className={classes.root}>
                <div
                    ref={cellDiv}
                    style={{
                        height: 1,
                        width,
                        display: 'block',
                        position: 'absolute',
                        alignItems: 'center',
                        gap: '4px',
                        justifyContent: 'center',
                        top: 0,
                    }}
                />
                <ReasonWrapper width={!!width && width > 20 ? width - 20 : 0}>
                    <Text scale="bodyS500" color="positive">
                        {cellParams.value.status.paidOutsideApp}
                    </Text>
                    <Text
                        scale="caption500"
                        ref={cellValue}
                        color="disable"
                        style={{
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            width: '100%',
                        }}
                    >
                        {cellParams.value.paidOutsideAppReason}
                    </Text>
                </ReasonWrapper>
                {showPopper && (
                    <Popper
                        open={showFullCell && anchorEl !== null}
                        anchorEl={anchorEl}
                        style={{ width, marginLeft: -17, zIndex: 200 }}
                    >
                        <Paper
                            elevation={1}
                            style={{
                                minHeight: wrapper.current!.offsetHeight - 3,
                                background: theme.colors.surface.primary,
                                width,
                                padding: 8,
                                boxShadow:
                                    '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                            }}
                        >
                            <Text scale="bodyS500" color="positive">
                                {cellParams.value.status.paidOutsideApp}
                            </Text>
                            <Text scale="caption500" className="cellValue" color="disable">
                                {cellParams.value.paidOutsideAppReason}
                            </Text>
                        </Paper>
                    </Popper>
                )}
            </div>
        )
    }
    if (cellParams.value.paymentStatus === SUBMISSION_STATUS_ENUM.PAID)
        return <Text color="positive">{cellParams.value.status.paid}</Text>

    if (cellParams.value.paymentStatus === SUBMISSION_STATUS_ENUM.CLOSED)
        return <Text color="disable">{cellParams.value.status.closed}</Text>
    return <Text color="disable">{cellParams.value.status.notEligible}</Text>
})

const ReasonWrapper = styled.div<{ width?: number }>`
    width: ${({ width }) => (width ? width + 'px' : '100%')};
`

export default PayoutStatusCell
